import React from 'react';
import {
  Box,
  Button,
  Alert,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { Rule, Gateway, ValidationResult } from '../../types/dynamicRouting';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import SortableRuleCard from './SortableRuleCard';

interface RulesEditorProps {
  rules: Rule[];
  gateways: Gateway[];
  onChange: (rules: Rule[]) => void;
  validationResult?: ValidationResult;
  collapseSignal?: number;
}

const RulesEditor: React.FC<RulesEditorProps> = ({
  rules,
  gateways,
  onChange,
  validationResult,
  collapseSignal,
}) => {
  const handleAddRule = () => {
    const newRule: Rule = {
      id: crypto.randomUUID(),
      name: `Rule ${rules.length + 1}`,
      conditions: {},
      gatewayDistribution: [
        {
          gatewayId: gateways.length > 0 ? gateways[0].id : '',
          percentage: 100,
        },
      ],
      enabled: true,
    };
    onChange([...rules, newRule]);
  };

  const handleRemoveRule = (index: number) => {
    const newRules = rules.filter((_, i) => i !== index);
    onChange(newRules);
  };

  const handleRuleChange = (index: number, rule: Rule) => {
    const newRules = [...rules];
    newRules[index] = rule;
    onChange(newRules);
  };

  return (
    <Box>
      {rules.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No rules defined. Add at least one rule to create a valid configuration.
        </Alert>
      )}

      <SortableContext items={rules.map((r) => r.id) as any} strategy={verticalListSortingStrategy}>
        {rules.map((rule, index) => (
          <SortableRuleCard
            key={rule.id ?? `rule-${index}`}
            rule={rule}
            index={index}
            gateways={gateways}
            onChange={handleRuleChange}
            onRemove={handleRemoveRule}
            validationResult={validationResult}
            collapseSignal={collapseSignal}
            totalRules={rules.length}
          />
        ))}
      </SortableContext>

      <Button
        variant="outlined"
        startIcon={<Add />}
        onClick={handleAddRule}
        size="large"
        sx={{ mt: 2 }}
      >
        Add Rule
      </Button>
    </Box>
  );
};

export default RulesEditor;
